import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, ChevronLeft, Search, Sparkles } from 'lucide-react'
import VideoaulaCard from '../components/VideoaulaCard'
import api, { getApiError } from '../services/api'

function MateriaPage() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q')?.trim() || ''
  const [input, setInput] = useState(query)

  useEffect(() => setInput(query), [query])

  const catalogQuery = useQuery({
    queryKey: ['materia', id, 'catalogo'],
    queryFn: async () => {
      const [materiaResponse, modulosResponse] = await Promise.all([
        api.get(`/materias/${id}`),
        api.get(`/materias/${id}/modulos`)
      ])
      return {
        materia: materiaResponse.data.materia,
        modulos: modulosResponse.data.modulos
      }
    }
  })

  const videosQuery = useQuery({
    queryKey: ['materia', id, 'busca', query],
    queryFn: async () => (await api.get('/search', {
      params: { q: query, materia_id: id }
    })).data,
    enabled: Boolean(catalogQuery.data?.materia && query.length >= 2)
  })

  const temasSugeridos = useMemo(() => {
    const nomes = (catalogQuery.data?.modulos || []).flatMap(modulo =>
      (modulo.temas || []).map(tema => tema.nome)
    )
    return [...new Set(nomes)].slice(0, 12)
  }, [catalogQuery.data])

  const buscar = event => {
    event.preventDefault()
    const novoTermo = input.trim()
    setSearchParams(novoTermo ? { q: novoTermo } : {})
  }

  const escolherTema = tema => {
    setInput(tema)
    setSearchParams({ q: tema })
  }

  if (catalogQuery.isLoading) return <p className="text-sm text-gray-500">Carregando matéria...</p>
  if (catalogQuery.isError) {
    return <div className="card text-sm"><p className="text-red-600">{getApiError(catalogQuery.error, 'Não foi possível carregar esta matéria.')}</p><button type="button" className="btn-secondary mt-3" onClick={() => catalogQuery.refetch()}>Tentar novamente</button></div>
  }

  const { materia } = catalogQuery.data
  const videoaulas = videosQuery.data?.videoaulas || []

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white"><ChevronLeft size={14} /> Voltar às matérias</Link>

      <header className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 dark:border-zinc-800 dark:bg-zinc-900/50 lg:p-7">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-zinc-800" style={{ color: materia.cor_hex }}><BookOpen size={23} /></span>
          <div><p className="text-xs font-medium uppercase tracking-[0.16em] text-gray-400">Pesquisar dentro da matéria</p><h1 className="mt-1 text-2xl font-medium lg:text-3xl">{materia.nome}</h1><p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">Encontre videoaulas relacionadas apenas a esta matéria.</p></div>
        </div>

        <form onSubmit={buscar} className="mt-5 flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 focus-within:border-black focus-within:ring-1 focus-within:ring-black dark:border-zinc-700 dark:bg-zinc-950 dark:focus-within:border-white dark:focus-within:ring-white">
          <Search size={18} className="shrink-0 text-gray-400" />
          <input className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400" value={input} onChange={event => setInput(event.target.value)} placeholder={`O que você quer aprender em ${materia.nome}?`} />
          <button type="submit" className="btn-primary px-3 py-1.5 text-xs">Buscar</button>
        </form>
      </header>

      {temasSugeridos.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2"><Sparkles size={16} className="text-amber-500" /><div><h2 className="text-sm font-medium">Sugestões para começar</h2><p className="text-xs text-gray-400">Use um tema como ponto de partida ou pesquise livremente.</p></div></div>
          <div className="flex flex-wrap gap-2">
            {temasSugeridos.map(tema => <button key={tema} type="button" onClick={() => escolherTema(tema)} className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${query === tema ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'}`}>{tema}</button>)}
          </div>
        </section>
      )}

      <section>
        <div className="mb-3 flex items-end justify-between gap-4">
          <div><h2 className="text-base font-medium">{query ? `Resultados para “${query}”` : `Pesquise videoaulas de ${materia.nome}`}</h2><p className="mt-0.5 text-xs text-gray-400">Resultados ao vivo já contextualizados pela matéria.</p></div>
          {query && !videosQuery.isLoading && <span className="text-xs text-gray-400">{videoaulas.length} opções</span>}
        </div>

        {!query && <div className="card py-8 text-center"><Search size={24} className="mx-auto mb-2 text-gray-300" /><p className="text-sm font-medium">O que você quer aprender?</p><p className="mt-1 text-xs text-gray-500">Digite um assunto ou escolha uma sugestão acima.</p></div>}
        {videosQuery.isLoading && <p className="card text-sm text-gray-500">Buscando videoaulas de {materia.nome}...</p>}
        {videosQuery.isError && <div className="card text-sm"><p className="text-red-600">{getApiError(videosQuery.error, 'A busca desta matéria falhou.')}</p><button type="button" className="btn-secondary mt-3" onClick={() => videosQuery.refetch()}>Tentar novamente</button></div>}
        {videosQuery.data?.youtube_erro && <div className="card border-amber-200 bg-amber-50 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">{videosQuery.data.youtube_erro}</div>}
        {!videosQuery.isLoading && !videosQuery.isError && !videosQuery.data?.youtube_erro && videoaulas.length === 0 && <div className="card py-8 text-center"><Search size={24} className="mx-auto mb-2 text-gray-300" /><p className="text-sm text-gray-500">Nenhuma videoaula encontrada. Tente outro termo.</p></div>}
        {videoaulas.length > 0 && <div className="grid gap-3 xl:grid-cols-2">{videoaulas.map(video => <VideoaulaCard key={video.id} videoaula={video} />)}</div>}
      </section>
    </div>
  )
}

export default MateriaPage
